<!--
 * COPYRIGHT Transtron Inc 2022
 *
 * 機能名：GPSトラッカーマスタ画面
 *
 * 作成者：2021/11/19 ノア）黄
 * 最終修正者：2022/1/17 ノア）黄
 *
 -->
<template>
  <div class="tti-main-view">
    <!-- ヘッダー -->
    <section>
      <nav class="tti-main-header-shadow">
        <div class="pull-left">
          <el-button class="backButton" @click="onBackButtonClick" v-tooltip="getTooltip(tooltipType.back)">
            <tti-svg-icon icon-name="wo_034"></tti-svg-icon>
          </el-button>
          <div class="tti-viewTitle">
            <tti-svg-icon icon-name="wo_008"></tti-svg-icon>
            <span>{{ $t('master.gpsTrackerMaster.viewTitle') }}</span>
          </div>
        </div>
        <div class="pull-right">
          <el-button
            class="tti-btn-jump"
            @click="onRepliesCountButtonClick"
            v-tooltip="{
              content: `${$t('master.gpsTrackerMaster.trustedRepliesCountTip')}`,
              placement: 'bottom-start',
              delay: { show: 1000, hide: 0 }
            }"
          >
            <tti-svg-icon icon-name="wo_012"></tti-svg-icon>
            {{ $t('master.gpsTrackerMaster.trustedRepliesCount') }}
            <tti-svg-icon icon-name="wo_035"></tti-svg-icon>
          </el-button>
        </div>
      </nav>
    </section>
    <!-- テーブル -->
    <section class="tti-padding-content">
      <div class="tti-content-title-container">
        <div class="tti-content-title">
          <span class="contentTitle">{{ $t('master.gpsTrackerMaster.contentTitle') }}</span>
          <el-select
            class="tti-viewSelect"
            v-model="businessOffice"
            size="small"
            :placeholder="$t('master.viewTitle.placeHolderOfOfficeSelector')"
            @change="onBusinessOfficeChange"
            v-tooltip="{
              content: `${$t('master.viewTitle.placeHolderOfOfficeSelector')}`,
              placement: 'bottom-start',
              delay: { show: 1000, hide: 0 }
            }"
          >
            <el-option
              v-for="item in businessOfficeMasterData"
              :key="item.id"
              :label="getBusinessOfficeLabel(item)"
              :value="item.id"
            ></el-option>
          </el-select>
          <span>{{ tableData.length }}</span>
          <span class="viewTitle">{{ $t('master.viewTitle.subject') }}</span>
          <el-checkbox
            class="displayDeleteDataCheckbox"
            v-model="isDeleteDataDisplay"
            @change="onDeleteDataDisplayChange"
            v-tooltip="{
              content: `${$t('master.gpsTrackerMaster.deleteDataDisplayTip')}`,
              placement: 'bottom-start',
              delay: { show: 1000, hide: 0 }
            }"
          >
            {{ $t('master.gpsTrackerMaster.deleteDataDisplay') }}
          </el-checkbox>
        </div>
        <div class="functionArea">
          <el-pagination
            background
            layout="prev, pager, next"
            :total="tableData.length"
            :page-size="visablePageSize"
            :hide-on-single-page="true"
            :current-page.sync="currentPage"
            @current-change="onCurrentPageChange"
          >
          </el-pagination>
          <div class="searchBox">
            <el-input v-model.trim="searchText" onkeyup="this.value=this.value.replace(/[,]/g,'')">
              <el-button slot="append" icon="el-icon-search" @click="onSearchBoxClick"></el-button>
            </el-input>
          </div>
        </div>
      </div>
      <el-form :model="form" ref="form" :disabled="pageLock">
        <el-table
          ref="tableRef"
          :data="visableTableData"
          height="calc(100vh - 226px)"
          border
          highlight-current-row
          :stripe="true"
          header-row-class-name="tti-master-table-header"
          :row-class-name="tableRowClassName"
          :empty-text="$t('messages.common.emptyTableData')"
          @selection-change="handleSelectionChange"
          :cell-class-name="getCellClassName"
          @sort-change="handleSortChange"
        >
          <el-table-column type="selection" :selectable="selectable" width="35"></el-table-column>
          <!-- SigfoxID -->
          <el-table-column
            class-name="required"
            prop="sigfoxId"
            :label="$t('master.gpsTrackerMaster.sigfoxId')"
            width="170"
            :sortable="true"
          >
            <template slot-scope="scope">
              <el-form-item :prop="'tableData[' + scope.$index + '].sigfoxId'" :rules="rules.sigfoxId">
                <el-input
                  :id="'tableData[' + scope.$index + '].sigfoxId'"
                  :disabled="!canCodeChange(scope.row.dataType)"
                  v-model.trim="scope.row.sigfoxId"
                  @change="onRepeatItemChange(formRef, 'sigfoxId')"
                  size="small"
                ></el-input>
              </el-form-item>
            </template>
          </el-table-column>
          <!-- シリアル番号 -->
          <el-table-column prop="serialNo" :label="$t('master.gpsTrackerMaster.serialNo')" width="300" :sortable="true">
            <template slot-scope="scope">
              <el-form-item :prop="'tableData[' + scope.$index + '].serialNo'" :rules="rules.serialNo">
                <el-input
                  :id="'tableData[' + scope.$index + '].serialNo'"
                  v-model.trim="scope.row.serialNo"
                  @change="onRepeatItemChange(formRef, 'serialNo')"
                  size="small"
                ></el-input>
              </el-form-item>
            </template>
          </el-table-column>
          <!-- 利用開始日 -->
          <el-table-column
            class-name="required"
            prop="startUseDate"
            :label="$t('master.gpsTrackerMaster.startUseDate')"
            width="170"
            :sortable="true"
          >
            <template slot-scope="scope">
              <el-form-item :prop="'tableData[' + scope.$index + '].startUseDate'" :rules="rules.startUseDate">
                <el-date-picker
                  :id="'tableData[' + scope.$index + '].startUseDate'"
                  v-model="scope.row.startUseDate"
                  type="date"
                  format="yyyy/MM/dd"
                  value-format="yyyy/MM/dd"
                  size="small"
                  :picker-options="pickerOptions"
                  :disabled="!canChange(scope.row)"
                  :editable="false"
                  :sortable="true"
                  :clearable="false"
                ></el-date-picker>
              </el-form-item>
            </template>
          </el-table-column>
          <!--  節電モード -->
          <el-table-column
            prop="powerSavingMode"
            :label="$t('master.gpsTrackerMaster.powerSavingMode')"
            width="200"
            :sortable="true"
          >
            <template slot-scope="scope">
              {{ getPowerSavingModeLabel(scope.row.powerSavingMode) }}
            </template>
          </el-table-column>
          <!-- 最終更新日 -->
          <el-table-column
            prop="lastUpdateDate"
            :label="$t('master.gpsTrackerMaster.lastUpdateDate')"
            width="170"
            :sortable="true"
          >
          </el-table-column>
          <!-- 累計通信回数 -->
          <el-table-column
            prop="sendCount"
            :label="$t('master.gpsTrackerMaster.sendCount')"
            width="150"
            align="right"
            :sortable="true"
          >
          </el-table-column>
          <!-- 前々月通信回数 -->
          <el-table-column
            prop="lastMonthBeforeSendCount"
            :label="$t('master.gpsTrackerMaster.lastMonthBeforeSendCount')"
            width="150"
            align="right"
            :sortable="true"
          >
            <template slot-scope="scope">
              <div :class="getBackGroundColorStyleByDate(scope.row, 'lastMonthBeforeSendCount')" size="small">
                {{ scope.row.lastMonthBeforeSendCount }}
              </div>
            </template>
          </el-table-column>
          <!-- 前月通信回数 -->
          <el-table-column
            prop="lastMonthSendCount"
            :label="$t('master.gpsTrackerMaster.lastMonthSendCount')"
            width="150"
            align="right"
            :sortable="true"
          >
            <template slot-scope="scope">
              <div :class="getBackGroundColorStyleByDate(scope.row, 'lastMonthSendCount')" size="small">
                {{ scope.row.lastMonthSendCount }}
              </div>
            </template>
          </el-table-column>
          <!-- 当月通信回数 -->
          <el-table-column
            prop="thisMonthSendCount"
            :label="$t('master.gpsTrackerMaster.thisMonthSendCount')"
            width="150"
            align="right"
            :sortable="true"
          >
            <template slot-scope="scope">
              <div :class="getBackGroundColorStyleByDate(scope.row, 'thisMonthSendCount')" size="small">
                {{ scope.row.thisMonthSendCount }}
              </div>
            </template>
          </el-table-column>
        </el-table>
      </el-form>
    </section>
    <!-- フッター -->
    <section class="tti-master-bottom-area">
      <div class="tti-bottom-left-area">
        <!-- 取消 -->
        <el-button
          class="tti-btn-default tti-btn-001"
          @click="onCancelButtonClick"
          :disabled="pageLock"
          v-tooltip="getTooltip(tooltipType.cancel)"
          ><tti-svg-icon icon-name="su_005"></tti-svg-icon>{{ $t('buttons.cancel') }}</el-button
        >
        <!-- 削除 -->
        <el-button
          class="tti-btn-default tti-btn-005"
          @click="onDeleteButtonClick"
          :disabled="pageLock"
          v-tooltip="getTooltip(tooltipType.delete)"
          ><tti-svg-icon icon-name="su_013"></tti-svg-icon>{{ $t('buttons.delete') }}</el-button
        >
        <!-- コピー -->
        <el-button
          class="tti-btn-default tti-btn-005"
          @click="onCopyButtonClick"
          :disabled="pageLock"
          v-tooltip="getTooltip(tooltipType.copy)"
        >
          <tti-svg-icon icon-name="su_012"></tti-svg-icon>{{ $t('buttons.copy') }}
        </el-button>
        <!-- 追加 -->
        <el-button
          class="tti-btn-default tti-btn-005"
          @click="onInsertButtonClick"
          :disabled="pageLock"
          v-tooltip="getTooltip(tooltipType.insert)"
        >
          <tti-svg-icon icon-name="su_011"></tti-svg-icon>{{ $t('buttons.insert') }}
        </el-button>
        <!-- テンプレート -->
        <el-button
          class="tti-btn-default tti-btn-006"
          @click="onTamplateButtonClick"
          :disabled="pageLock"
          v-tooltip="getTooltip(tooltipType.template)"
        >
          {{ $t('buttons.template') }}
        </el-button>
      </div>
      <div class="tti-bottom-right-area">
        <!-- 入力 -->
        <el-button
          class="tti-btn-default tti-btn-001"
          @click="onInputButtonClick"
          :disabled="pageLock"
          v-tooltip="getTooltip(tooltipType.input)"
        >
          <tti-svg-icon icon-name="su_004"></tti-svg-icon>{{ $t('buttons.input') }}
        </el-button>
        <!-- 出力 -->
        <el-button
          class="tti-btn-default tti-btn-001"
          @click="onOutputButtonClick"
          :disabled="pageLock"
          v-tooltip="getTooltip(tooltipType.output)"
        >
          <tti-svg-icon icon-name="su_003"></tti-svg-icon>{{ $t('buttons.output') }}
        </el-button>
        <!-- 保存 -->
        <el-button
          class="tti-btn-default tti-btn-001"
          @click="onSaveButtonClick"
          :disabled="pageLock"
          v-tooltip="getTooltip(tooltipType.save)"
        >
          <tti-svg-icon icon-name="su_002"></tti-svg-icon>{{ $t('buttons.save') }}
        </el-button>
        <!-- file -->
        <input ref="inputFile" type="file" @change="onFileChange" class="fileInput" accept=".csv" />
      </div>
    </section>

    <!-- 受信回数カウント -->
    <ttiDialog
      :isShow="showDialog"
      @handelDisplay="onHandelDisplay"
      :manualKey="manualKey"
      :title="$t('master.gpsTrackerMaster.trustedRepliesCount')"
      width="500px"
    >
      <template slot="body">
        <el-form
          :model="trustedRepliesCountForm"
          :rules="trustedRepliesCountRules"
          ref="trustedRepliesCountFormRef"
          label-width="150px"
          size="mini"
        >
          <!-- 出力種類 -->
          <el-form-item>
            <el-radio-group v-model="trustedRepliesCountForm.type" @change="onTrustedRepliesCountTypeChange">
              <el-radio v-for="item in trustedRepliesCountTypeList" :key="item.value" :label="item.value">
                {{ item.label }}
              </el-radio>
            </el-radio-group>
          </el-form-item>
          <!-- 日別 -->
          <template v-if="trustedRepliesCountForm.type === trustedRepliesCountType.DAY">
            <!-- 運行開始日 -->
            <el-form-item :label="$t('master.gpsTrackerMaster.operationStartDate')" prop="operationStartDate">
              <el-date-picker
                v-model="trustedRepliesCountForm.operationStartDate"
                type="date"
                format="yyyy/MM/dd"
                value-format="yyyy/MM/dd"
                size="small"
                :picker-options="pickerOptions"
                :editable="false"
                :clearable="false"
                v-tooltip="getTooltip(tooltipType.operationStartDayTip)"
              ></el-date-picker>
            </el-form-item>
            <!-- 運行終了日 -->
            <el-form-item :label="$t('master.gpsTrackerMaster.operationEndDate')" prop="operationEndDate">
              <el-date-picker
                v-model="trustedRepliesCountForm.operationEndDate"
                type="date"
                format="yyyy/MM/dd"
                value-format="yyyy/MM/dd"
                size="small"
                :picker-options="pickerOptions"
                :editable="false"
                :clearable="false"
                v-tooltip="getTooltip(tooltipType.operationEndDayTip)"
              ></el-date-picker>
            </el-form-item>
          </template>
          <!-- 月別 -->
          <template v-if="trustedRepliesCountForm.type === trustedRepliesCountType.MONTH">
            <!-- 運行開始月 -->
            <el-form-item :label="$t('master.gpsTrackerMaster.operationStartMonth')" prop="operationStartMonth">
              <el-date-picker
                v-model="trustedRepliesCountForm.operationStartMonth"
                type="month"
                format="yyyy/MM"
                value-format="yyyy/MM"
                size="small"
                :picker-options="pickerOptions"
                :editable="false"
                :clearable="false"
                v-tooltip="getTooltip(tooltipType.operationStartMonthTip)"
              ></el-date-picker>
            </el-form-item>
            <!-- 運行終了月 -->
            <el-form-item :label="$t('master.gpsTrackerMaster.operationEndMonth')" prop="operationEndMonth">
              <el-date-picker
                v-model="trustedRepliesCountForm.operationEndMonth"
                type="month"
                size="small"
                format="yyyy/MM"
                value-format="yyyy/MM"
                :picker-options="pickerOptions"
                :editable="false"
                :clearable="false"
                v-tooltip="getTooltip(tooltipType.operationEndMonthTip)"
              ></el-date-picker>
            </el-form-item>
          </template>
        </el-form>
      </template>
      <template slot="foot">
        <!-- 出力 -->
        <el-button
          class="tti-btn-default tti-btn-001"
          @click="onDialogOutputButtonClick"
          v-tooltip="getTooltip(tooltipType.output)"
        >
          <tti-svg-icon icon-name="su_003"></tti-svg-icon>{{ $t('buttons.output') }}
        </el-button>
      </template>
    </ttiDialog>
  </div>
</template>

<script lang="ts">
import { Component, Ref } from 'vue-property-decorator'
import { cloneDeep } from 'lodash'
import { format, differenceInDays, differenceInMonths, getDaysInMonth } from 'date-fns'
import { Form, Table } from 'element-ui'
import { cellCallbackParams } from 'element-ui/types/table'

import MST_BASE, { FormItemType } from '@/views/master/MST_BASE.vue'
import TtiDialog from '@/components/TtiDialog.vue'
import { i18n } from '@/locales'
import {
  DataType,
  TemplateCode,
  MasterType,
  TrustedRepliesCountType,
  PowerType,
  UserRole,
  DeleteType
} from '@/commons/constants'
import {
  getGPSTrackerMasterData,
  updateGPSTrackerMasterData,
  gpsTrackerMasterCSVDownload,
  gpsTrackerDateCounCSVDownload,
  gpsTrackerMonthCounCSVDownload
} from '@/store/apis'
import { GPSTrackerMasterData, BusinessOfficeMasterData, MasterData, TrustedRepliesCount, General } from '@/models'
import { RULES, VALID_PATTERN } from '@/service/validateService'
import { msgBoxService } from '@/commons/services/msgBoxService'
import HelperService from '@/service/helperService'
import { localService } from '@/commons/services/localService'
import { isDateAfter, parseDate, pickerOptions } from '@/utils/dateUtils'
import manualConfig from '@/config/manualConfig'
import { generalService, GeneralType } from '@/commons/services/generalService'

/**
 * 背景色
 *
 * @interface
 */
interface BackgroundColor {
  /**
   * 赤
   */
  redBackgroundColor: boolean
  /**
   * 黄色
   */
  yellowBackgroundColor: boolean
}

/**
 * GPSトラッカーマスタ画面
 *
 * @class
 */
@Component({
  name: 'mst-gps-tracker',
  components: { TtiDialog }
})
export default class MST_GPS_TRACKER extends MST_BASE {
  /**
   * タイムセレクタをクリックしたときの処理
   */
  pickerOptions = pickerOptions

  /**
   * 削除したGPSトラッカー表示用フラグ
   */
  isDeleteDataDisplay = false

  /**
   * マスタ画面ツールチップタイプ
   */
  tooltipType = {
    // 戻る
    back: 'back',
    // 取消
    cancel: 'cancel',
    // 削除
    delete: 'delete',
    // テンプレート
    template: 'template',
    // 追加
    insert: 'insert',
    // 保存
    save: 'save',
    // コピー
    copy: 'copy',
    // 出力
    output: 'output',
    // 入力
    input: 'input',
    // 運行開始日
    operationStartDayTip: 'operationStartDayTip',
    // 運行終了日
    operationEndDayTip: 'operationEndDayTip',
    // 運行開始月
    operationStartMonthTip: 'operationStartMonthTip',
    // 運行終了月
    operationEndMonthTip: 'operationEndMonthTip'
  }

  /**
  /**
   * フォームのref
   */
  @Ref('form') readonly formRef!: Form

  @Ref('tableRef') readonly tableRef!: Table

  /**
   * 画面ID
   */
  pageId = 'MST_GPS_TRACKER'

  /**
   * 第一列の項目（Focus用）
   */
  firstField = 'sigfoxId'

  /**
   * 画面名
   */
  pageName = i18n.t('master.gpsTrackerMaster.viewTitle').toString()

  /**
   * 最先のCSVヘッダー
   */
  firstCsvHeader = i18n.t('master.csvHeader.MST_GPS_TRACKER').toString()

  /**
   * テンプレートコード
   */
  templateCode = TemplateCode.MST_GPS_TRACKER

  /**
   * 営業所マスタリスト
   */
  businessOfficeMasterData: BusinessOfficeMasterData[] = []

  /**
   * 節電モードリスト
   */
  managementPowerTypeList: General[] = []

  /**
   * 受信回数カウントサブ画面のマニュアルキーを取得
   *
   * @returns {string} 受信回数カウントサブ画面のマニュアルキー
   */
  get manualKey(): string {
    return manualConfig.route.trustedRepliesCount
  }

  /**
   * 管理対象マスタ編集FORM
   */
  form = {
    tableData: this.tableData
  } as Record<string, GPSTrackerMasterData[]>

  /**
   * すべてのsigfox数
   */
  terminalCount = 0

  /**
   * 検証ルール
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  rules = {} as any

  /**
   * CSVヘッダーリスト
   */
  csvHeaders = [
    'sigfoxId',
    'serialNo',
    'startUseDate',
    'powerSavingMode',
    'lastUpdateDate',
    'sendCount',
    'lastMonthBeforeSendCount',
    'lastMonthSendCount',
    'thisMonthSendCount'
  ]

  // eslint-disable-next-line max-lines-per-function
  initRule(): void {
    this.rules = {
      // SigfoxID
      sigfoxId: [
        // 必須
        {
          required: true,
          message: i18n.t('vaildate.common.required', [i18n.t('master.gpsTrackerMaster.sigfoxId')]).toString(),
          csvMessage: 'vaildate.csvFile.required',
          messageEnterPoint: [i18n.t('master.gpsTrackerMaster.sigfoxId').toString()],
          trigger: 'blur'
        },
        // 半角英数字
        {
          pattern: VALID_PATTERN[RULES.ALPHANUMERIC],
          message: i18n
            .t('vaildate.common.fixedAlphanumericWithLength', [i18n.t('master.gpsTrackerMaster.sigfoxId'), 8])
            .toString(),
          csvMessage: 'vaildate.csvFile.alphanumeric',
          messageEnterPoint: [i18n.t('master.gpsTrackerMaster.sigfoxId').toString()],
          trigger: 'blur'
        },
        {
          validRule: [
            // 8文字
            {
              rule: RULES.RANGE,
              range: [8, 8],
              message: i18n
                .t('vaildate.common.fixedAlphanumericWithLength', [i18n.t('master.gpsTrackerMaster.sigfoxId'), 8])
                .toString(),
              csvMessage: 'vaildate.csvFile.numberWithLength',
              messageEnterPoint: [i18n.t('master.gpsTrackerMaster.sigfoxId').toString(), 8]
            },
            // 重複
            {
              rule: RULES.REPEAT,
              tableData: this.tableData,
              visableTableData: () => this.visableTableData,
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              isDisabled: (row: any) => !this.canCodeChange(row.dataType),
              tableColumn: 'sigfoxId',
              message: i18n.t('vaildate.common.repeat', [i18n.t('master.gpsTrackerMaster.sigfoxId')]).toString(),
              csvMessage: 'vaildate.csvFile.repeat',
              messageEnterPoint: [i18n.t('master.gpsTrackerMaster.sigfoxId').toString()]
            }
          ],
          trigger: 'blur'
        }
      ],
      // シリアル番号
      serialNo: [
        {
          validRule: [
            // 20文字以内
            {
              rule: RULES.MAXLENGTH,
              number: 20,
              message: i18n.t('vaildate.common.maxlength', [i18n.t('master.gpsTrackerMaster.serialNo'), 20]).toString(),
              csvMessage: 'vaildate.csvFile.numberWithLength',
              messageEnterPoint: [i18n.t('master.gpsTrackerMaster.serialNo').toString(), 20]
            },
            // 重複
            {
              rule: RULES.REPEAT,
              tableData: this.tableData,
              tableColumn: 'serialNo',
              message: i18n.t('vaildate.common.repeat', [i18n.t('master.gpsTrackerMaster.serialNo')]).toString(),
              csvMessage: 'vaildate.csvFile.repeat',
              messageEnterPoint: [i18n.t('master.gpsTrackerMaster.serialNo').toString()]
            }
          ],
          trigger: 'blur'
        }
      ],
      // 利用開始日
      startUseDate: [
        // 必須
        {
          required: true,
          message: i18n.t('vaildate.common.required', [i18n.t('master.gpsTrackerMaster.startUseDate')]).toString(),
          csvMessage: 'vaildate.csvFile.required',
          messageEnterPoint: [i18n.t('master.gpsTrackerMaster.startUseDate').toString()],
          trigger: 'blur'
        },
        // 日付
        {
          pattern: VALID_PATTERN[RULES.DATE],
          message: i18n.t('vaildate.common.date', [i18n.t('master.gpsTrackerMaster.startUseDate')]).toString(),
          csvMessage: 'vaildate.csvFile.date',
          messageEnterPoint: [i18n.t('master.gpsTrackerMaster.startUseDate').toString()],
          trigger: 'blur'
        }
      ]
    }
  }

  /**
   * インスタンスがマウントされたちょうど後にフック処理
   *
   * @returns {void}
   */
  async mounted(): Promise<void> {
    this.$log.debug('MST_GPS_TRACKER > mounted')
    this.initFormTemplate()
    this.fetchManagementPowerType()
    this.fetchBusinessOfficeMaster()
    this.initFormRef(this.formRef)
    this.initTableRef(this.tableRef)
    this.initSearchTargets()
  }

  protected fetchTableDataBefore(): () => Promise<MasterData> {
    return async () => {
      const params = {
        type: MasterType.DETAIL,
        businessOfficeId: this.businessOffice,
        deleteFlg: this.isDeleteDataDisplay ? DeleteType.deleteData : DeleteType.notdeleteData
      }

      const res = await getGPSTrackerMasterData(params)

      this.terminalCount = res.count

      return res
    }
  }

  fetchTableDataAfter(): void {
    this.initRule()
    this.initValidate(this.rules)
    this.multipleSelection = []
  }

  // eslint-disable-next-line max-lines-per-function
  getBackGroundColorStyleByDate(row: GPSTrackerMasterData, key: string): BackgroundColor {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    let target = row[key]
    if (!target) {
      return { redBackgroundColor: false, yellowBackgroundColor: false }
    }

    // 節電モード
    if (key === 'powerSavingMode') {
      if (target === PowerType.MODE1 || target === PowerType.MODE2) {
        return { redBackgroundColor: true, yellowBackgroundColor: false }
      }

      if (target === PowerType.EXCHANGE) {
        return { redBackgroundColor: false, yellowBackgroundColor: true }
      }

      return { redBackgroundColor: false, yellowBackgroundColor: false }
    }

    const currentMonth = new Date().getMonth()
    let days = 0
    let targetMonth = 0

    // 前々月通信回数
    if (key === 'lastMonthBeforeSendCount') {
      targetMonth = currentMonth - 2 >= 0 ? currentMonth - 2 : currentMonth + 10
    }

    // 前月通信回数
    if (key === 'lastMonthSendCount') {
      targetMonth = currentMonth - 1 >= 0 ? currentMonth - 1 : currentMonth + 11
    }

    // 当月通信回数
    if (key === 'thisMonthSendCount') {
      targetMonth = currentMonth
    }

    days = getDaysInMonth(new Date(new Date().getFullYear(), targetMonth))
    if (target > days * 10) {
      return { redBackgroundColor: true, yellowBackgroundColor: false }
    } else {
      return { redBackgroundColor: false, yellowBackgroundColor: false }
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getCsvRule(): any {
    return this.rules
  }

  /**
   * 節電モードリストを取得する
   *
   * @returns {void}
   */
  async fetchManagementPowerType(): Promise<void> {
    try {
      this.managementPowerTypeList = await generalService[GeneralType.POWER_SAVING_MODE]
    } catch (e) {
      this.$log.debug('MST_GPS_TRACKER > fetchManagementPlaceType: e=', e)
      throw e
    }
  }

  /**
   * 利用開始日が変更できるかを判断する
   *
   * @param {GPSTrackerMasterData} row GPSトラッカーマスタ
   * @returns {boolean} 変更できるかどうか
   */
  canChange(row: GPSTrackerMasterData): boolean {
    // データを削除する場合
    if (row.deleteFlg === DeleteType.deleteData) {
      return false
    }
    // システム権限の場合「利用開始日」は変更可能とする
    if (localService.userInfo.role === UserRole.SYSTEM) {
      return true
    }
    return row.dataType === DataType.INSERT
  }

  /**
   * 削除前に、削除チェックを行う。
   *
   * @returns {boolean} チェック結果
   */
  deleteTableDataBefore(): boolean {
    let canDelete = true
    for (const data of this.multipleSelection) {
      if (data.hasManagementTarget) {
        canDelete = false
        msgBoxService.info(
          i18n
            .t('messages.masterCommon.cantDelete', [
              data.sigfoxId,
              i18n.t('master.gpsTrackerMaster.managementTarget').toString()
            ])
            .toString()
        )
        break
      }
    }

    return canDelete
  }

  /**
   * 登録できる件数をチェックする
   *
   * @returns {boolean} true: 登録できる件数が最大件数を超えていない
   */
  checkInsert(): boolean {
    if (this.terminalCount >= parseInt(localService.userInfo?.terminalUpperLimit)) {
      msgBoxService.info(
        i18n.t('messages.masterCommon.maxCount', [this.pageName, localService.userInfo?.terminalUpperLimit]).toString()
      )
      return false
    }

    return true
  }

  /**
   * 追加ボタンをクリックした時の処理。
   *
   * @returns {void}
   */
  onInsertButtonClick(): void {
    this.$log.debug(`MST_GPS_TRACKER > onInsertButtonClick`)
    if (!this.checkInsert()) {
      return
    }

    const item = {
      ...this.formTemplate,
      dataType: DataType.INSERT,
      startUseDate: format(new Date(), 'yyyy/MM/dd'),
      index: this.tableData.length
    }

    this.tableData.push(item)
    this.visableTableData.push(item)
    this.clearValidate()
    this.insertAfter()
  }

  /**
   * 保存前に、チェックを行う
   *
   * @returns {Promise<boolean>} チェック結果
   */
  protected async saveTableDataBefore(): Promise<boolean> {
    return this.dataValidate()
  }

  /**
   * 保存API
   *
   * @returns {void}
   */
  async saveTableDataAPI(): Promise<void> {
    const params = this.getUpsertRequest()
    await updateGPSTrackerMasterData(params)
  }

  /**
   * 出力API
   *
   * @returns {void}
   */
  async tableDataOutputAPI(): Promise<void> {
    const params = {
      businessOfficeId: this.businessOffice,
      deleteFlg: this.isDeleteDataDisplay ? DeleteType.deleteData : DeleteType.notdeleteData
    }
    const res = await gpsTrackerMasterCSVDownload(params)
    HelperService.download(res.fileName, res.data)
  }

  /**
   * コピーボタンをクリックした時の処理。
   *
   * @returns {void}
   */
  onCopyButtonClick(): void {
    this.$log.debug(`${this.pageId} > onCopyButtonClick`)
    if (!this.multipleSelection || this.multipleSelection.length === 0) {
      msgBoxService.info(i18n.t('messages.masterCommon.noDataSelected').toString())
      return
    }

    if (!this.tableData) {
      return
    }

    if (this.terminalCount + this.multipleSelection.length > parseInt(localService.userInfo?.terminalUpperLimit)) {
      msgBoxService.info(
        i18n.t('messages.masterCommon.maxCount', [this.pageName, localService.userInfo?.terminalUpperLimit]).toString()
      )
      return
    }

    const multipleSelection = this.multipleSelection.map((item, index) => {
      return {
        ...item,
        id: '',
        dataType: DataType.INSERT,
        hasManagementTarget: false,
        lastUpdateDate: '',
        sendCount: '',
        lastMonthBeforeSendCount: '',
        lastMonthSendCount: '',
        thisMonthSendCount: '',
        powerSavingMode: '',
        index: this.tableData.length + index
      }
    })

    const multipleSelectionClone = cloneDeep(multipleSelection)

    this.tableData.push(...multipleSelectionClone)
    this.visableTableData.push(...multipleSelectionClone)
    this.insertAfter()
  }

  /**
   * CSVデータ合併前の処理
   *
   * @param {any[]} dataList データ
   * @returns {void}
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  csvDataMergeIntoTableDataBefore(dataList: any[]): void {
    dataList.forEach(item => {
      item.dataType = DataType.INSERT
    })
  }

  /**
   * 受信回数カウントボタンをクリックした時の処理。
   *
   * @returns {void}
   */
  onRepliesCountButtonClick(): void {
    this.$log.debug(`MST_GPS_TRACKER > onRepliesCountButtonClick`)
    if (!this.checkDataChanged()) {
      this.showDialog = true
      return
    }

    const confirmCallback = async () => {
      if (await this.saveTableData()) {
        this.showDialog = true
      }
    }

    const rejectCallback = () => {
      this.showDialog = true
    }

    msgBoxService.confirmWithCancel(
      i18n.t('messages.masterCommon.saveConfirmWhenChanged').toString(),
      confirmCallback,
      rejectCallback
    )
  }

  initSearchTargets(): void {
    this.searchTargets = [
      {
        type: FormItemType.Input,
        field: 'sigfoxId'
      },
      {
        type: FormItemType.Input,
        field: 'serialNo'
      },
      {
        type: FormItemType.Input,
        field: 'startUseDate'
      },
      {
        type: FormItemType.Select,
        field: 'powerSavingMode',
        list: () => {
          return this.managementPowerTypeList
        }
      },
      {
        type: FormItemType.Input,
        field: 'lastUpdateDate'
      },
      {
        type: FormItemType.Input,
        field: 'sendCount'
      },
      {
        type: FormItemType.Input,
        field: 'lastMonthBeforeSendCount'
      },
      {
        type: FormItemType.Input,
        field: 'lastMonthSendCount'
      },
      {
        type: FormItemType.Input,
        field: 'thisMonthSendCount'
      }
    ]
  }

  /**
   * 受信回数カウント
   */
  @Ref() readonly trustedRepliesCountFormRef!: Form

  /**
   * 受信回数カウント画面表示
   */
  showDialog = false

  /**
   * 表示状態変更
   *
   * @returns {void}
   */
  onHandelDisplay(): void {
    this.showDialog = !this.showDialog
    this.trustedRepliesCountForm = {
      type: TrustedRepliesCountType.DAY,
      operationStartDate: '',
      operationEndDate: '',
      operationStartMonth: '',
      operationEndMonth: '',
      deleteFlg: this.isDeleteDataDisplay ? DeleteType.deleteData : DeleteType.notdeleteData
    }
    this.trustedRepliesCountFormRef.clearValidate()
  }

  trustedRepliesCountForm: TrustedRepliesCount = {
    type: TrustedRepliesCountType.DAY,
    operationStartDate: '',
    operationEndDate: '',
    operationStartMonth: '',
    operationEndMonth: '',
    deleteFlg: this.isDeleteDataDisplay ? DeleteType.deleteData : DeleteType.notdeleteData
  }

  trustedRepliesCountType = TrustedRepliesCountType

  trustedRepliesCountRules = {
    // 運行開始日
    operationStartDate: [
      {
        required: true,
        message: i18n.t('vaildate.common.required', [i18n.t('master.gpsTrackerMaster.operationStartDate')]).toString(),
        trigger: 'blur'
      }
    ],
    // 運行終了日
    operationEndDate: [
      {
        required: true,
        message: i18n.t('vaildate.common.required', [i18n.t('master.gpsTrackerMaster.operationEndDate')]).toString(),
        trigger: 'blur'
      }
    ],
    // 運行開始月
    operationStartMonth: [
      {
        required: true,
        message: i18n.t('vaildate.common.required', [i18n.t('master.gpsTrackerMaster.operationStartMonth')]).toString(),
        trigger: 'blur'
      }
    ],
    // 運行終了月
    operationEndMonth: [
      {
        required: true,
        message: i18n.t('vaildate.common.required', [i18n.t('master.gpsTrackerMaster.operationEndMonth')]).toString(),
        trigger: 'blur'
      }
    ]
  }

  trustedRepliesCountTypeList = [
    {
      value: TrustedRepliesCountType.DAY,
      label: i18n.t('master.gpsTrackerMaster.typeDay')
    },
    {
      value: TrustedRepliesCountType.MONTH,
      label: i18n.t('master.gpsTrackerMaster.typeMonth')
    }
  ]

  /**
   * 出力種類変更
   *
   * @returns {void}
   */
  onTrustedRepliesCountTypeChange(): void {
    this.trustedRepliesCountFormRef.clearValidate()
  }

  /**
   * 出力前に、削除チェックを行う。
   *
   * @returns {boolean} チェック結果
   */
  // eslint-disable-next-line max-lines-per-function
  async dialogFormCheck(): Promise<boolean> {
    let valid = await this.trustedRepliesCountFormRef.validate()
    if (!valid) {
      return valid
    }

    // 日別
    if (this.trustedRepliesCountForm.type === TrustedRepliesCountType.DAY) {
      // 運行開始日は運行終了日を超える場合
      if (isDateAfter(this.trustedRepliesCountForm.operationStartDate, this.trustedRepliesCountForm.operationEndDate)) {
        msgBoxService.info(
          i18n
            .t('vaildate.common.dateRange', [
              i18n.t('master.gpsTrackerMaster.operationStartDate'),
              i18n.t('master.gpsTrackerMaster.operationEndDate')
            ])
            .toString()
        )
        return false
      }

      // 運行開始日から運行終了日までの選択された期間は31日を超える場合
      const diff = differenceInDays(
        parseDate(this.trustedRepliesCountForm.operationEndDate),
        parseDate(this.trustedRepliesCountForm.operationStartDate)
      )

      if (diff > 31) {
        msgBoxService.info(i18n.t('vaildate.common.differenceInDays', [31]).toString())
        return false
      }
      // 月別
    } else {
      // 運行開始月は運行終了月を超える場合
      if (
        isDateAfter(
          this.trustedRepliesCountForm.operationStartMonth + '/01',
          this.trustedRepliesCountForm.operationEndMonth + '/01'
        )
      ) {
        msgBoxService.info(
          i18n
            .t('vaildate.common.dateRange', [
              i18n.t('master.gpsTrackerMaster.operationStartMonth'),
              i18n.t('master.gpsTrackerMaster.operationEndMonth')
            ])
            .toString()
        )
        return false
      }

      // 運行開始月から運行終了月までの選択された期間は13月を超える場合
      const diff = differenceInMonths(
        parseDate(this.trustedRepliesCountForm.operationEndMonth + '/01'),
        parseDate(this.trustedRepliesCountForm.operationStartMonth + '/01')
      )

      if (diff > 13) {
        msgBoxService.info(i18n.t('vaildate.common.differenceInMonths', [13]).toString())
        return false
      }
    }

    return true
  }

  /**
   * 出力ボタンをクリックした時の処理。
   *
   * @returns {void}
   */
  async onDialogOutputButtonClick(): Promise<void> {
    this.$log.debug(`MST_GPS_TRACKER > onDialogOutputButtonClick`)
    if (!(await this.dialogFormCheck())) {
      return
    }

    let res
    this.trustedRepliesCountForm.businessOfficeId = this.businessOffice
    this.trustedRepliesCountForm.deleteFlg = this.isDeleteDataDisplay ? DeleteType.deleteData : DeleteType.notdeleteData
    if (this.trustedRepliesCountForm.type === TrustedRepliesCountType.DAY) {
      res = await gpsTrackerDateCounCSVDownload(this.trustedRepliesCountForm)
    } else {
      res = await gpsTrackerMonthCounCSVDownload(this.trustedRepliesCountForm)
    }

    HelperService.download(res.fileName, res.data)
  }

  /**
   * チェックボックスをクリックしたときの処理。
   *
   * @returns {void}
   */
  async onDeleteDataDisplayChange(): Promise<void> {
    if (!this.checkDataChanged()) {
      this.fetchTableData()
      return
    }

    const confirmCallback = async () => {
      try {
        if (!(await this.saveTableData())) {
          this.isDeleteDataDisplay = !this.isDeleteDataDisplay
          return
        }
      } catch (e) {
        this.isDeleteDataDisplay = !this.isDeleteDataDisplay
        return
      }

      this.fetchTableData()
    }

    const rejectCallback = () => {
      this.fetchTableData()
    }

    const cancelCallback = () => {
      this.isDeleteDataDisplay = !this.isDeleteDataDisplay
    }

    msgBoxService.confirmWithCancel(
      i18n.t('messages.masterCommon.saveConfirmWhenChanged').toString(),
      confirmCallback,
      rejectCallback,
      cancelCallback
    )
  }

  /**
   * 削除データのチェックボックスを設定する。
   *
   * @param {GPSTrackerMasterData} row GPSトラッカーマスタデータ
   * @returns {boolean} チェック結果
   */
  selectable(row: GPSTrackerMasterData): boolean {
    return row.deleteFlg !== DeleteType.deleteData
  }

  /**
   * 節電モード表示名を取得する
   *
   * @param {string} code 時間
   * @returns {string} 表示名
   */
  getPowerSavingModeLabel(code: string): string {
    const format = {
      code: 'code',
      value: 'name'
    }

    return HelperService.getDefinitionName(this.managementPowerTypeList, code, format)
  }

  /**
   * テーブル背景色を取得する
   *
   * @param {cellCallbackParams} item テーブルデータ
   * @returns {string} 背景色Class
   */
  getCellClassName(item: cellCallbackParams): string {
    const { property } = item.column as { property: string }
    const value = (item.row as Record<string, string>)[property]

    // 節電モード
    if (property === 'powerSavingMode') {
      return this.getBackGroundColorStyle(value)
    }
    return ''
  }

  /**
   * 節電モード
   *
   * @param {string[]} powerSavingMode 時間
   * @returns {string} 背景色
   */
  getBackGroundColorStyle(powerSavingMode: string): string {
    if (!powerSavingMode) {
      return ''
    }

    if (powerSavingMode === PowerType.MODE1 || powerSavingMode === PowerType.MODE2) {
      return 'redBackgroundColor'
    }

    if (powerSavingMode === PowerType.EXCHANGE) {
      return 'yellowBackgroundColor'
    }

    return ''
  }

  /**
   * 入力FORMのテンプレートを初期化する
   *
   * @returns {void}
   */
  initFormTemplate(): void {
    this.formTemplate = {
      sigfoxId: '',
      serialNo: '',
      startUseDate: ''
    }
  }
}
</script>

<style lang="scss" scoped>
::v-deep .cell {
  text-overflow: unset;
}

::v-deep .tti-bottom-right-area .tti-btn-default {
  margin-right: 0 !important;
}

.tti-btn-jump {
  background-color: $C43;
  height: 100%;
  float: right;
  padding: 0 6px;

  &:active {
    @include bt_001_p;
  }
}

.fileInput {
  display: none;
}

.tti-main-header-shadow {
  display: flex;
  justify-content: space-between;
}

::v-deep .redBackgroundColor {
  background-color: $C23 !important;
}

::v-deep .yellowBackgroundColor {
  background-color: $C24 !important;
}

.displayDeleteDataCheckbox {
  margin-left: 48px;

  ::v-deep span {
    font-size: $FS01;
  }
}
</style>
